import React, { useState, useEffect } from 'react';
import { Search, MapPin, TrendingDown, Utensils, Heart, Flame, Loader2, Star, Clock, Zap, Leaf, ArrowRight, CheckCircle, ArrowDown, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const TRENDING = [
  "Butter Chicken", "Biryani", "Masala Dosa", "Paneer Tikka",
  "Chicken Fried Rice", "Margherita Pizza", "Vada Pav", "Chole Bhature",
  "Dal Makhani", "Hakka Noodles",
];

const FoodPriceCompare = () => {
  const [dishName, setDishName] = useState('');
  const [location, setLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => { detectLocation(); }, []);

  const detectLocation = () => {
    setLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude: lat, longitude: lng } = pos.coords;
          setLocation({ lat, lng });
          setLocating(false);
          try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
            const data = await res.json();
            setLocationName(data.address?.suburb || data.address?.city_district || data.address?.city || `${lat.toFixed(2)}, ${lng.toFixed(2)}`);
          } catch { setLocationName(`${lat.toFixed(2)}, ${lng.toFixed(2)}`); }
          toast.success('Location detected!');
        },
        () => { setLocating(false); setLocation({ lat: 12.9716, lng: 77.5946 }); setLocationName('Bangalore (default)'); toast.info('Using default location. Enable GPS for accuracy.'); },
        { timeout: 10000, enableHighAccuracy: true }
      );
    } else { setLocating(false); setLocation({ lat: 12.9716, lng: 77.5946 }); setLocationName('Bangalore'); }
  };

  const searchDish = async (name) => {
    const q = name || dishName;
    if (!q.trim()) { toast.error('Enter a dish name'); return; }
    if (!location) { toast.error('Waiting for location...'); return; }
    setLoading(true); setResults(null); setDishName(q);
    try {
      const r = await axios.post(`${API}/food-compare/search`, { dish_name: q, lat: location.lat, lng: location.lng });
      setResults(r.data);
      const matched = r.data.matched?.length || 0;
      const total = r.data.total_results || 0;
      toast.success(matched > 0 ? `${matched} restaurants found on both platforms! (${total} total)` : `${total} results found`);
    } catch (err) { toast.error('Search failed'); }
    finally { setLoading(false); }
  };

  const n = results?.nutrition_info;
  const bd = results?.best_deal;

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="food-compare-page">
      <Toaster position="top-right" richColors theme="dark" />

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#09090b]/80 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/dashboard" className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center hover:scale-110 transition-transform" title="Back to HealthAI" data-testid="back-to-healthai">
              <Heart className="w-4 h-4 text-white" />
            </a>
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center"><Utensils className="w-4 h-4" /></div>
            <div><h1 className="text-base font-bold tracking-tight">FoodCompare AI</h1><p className="text-[10px] text-zinc-500">Same dish. Same restaurant. Best price.</p></div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/dashboard" className="text-xs text-zinc-500 hover:text-white transition hidden sm:block">Back to HealthAI</a>
            <div className="flex items-center gap-1.5 text-xs text-zinc-400">
              <MapPin className="w-3.5 h-3.5 text-orange-400" />
              {locating ? <span className="animate-pulse">Detecting...</span> : <button onClick={detectLocation} className="hover:text-white transition">{locationName || 'Set location'}</button>}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold mb-2 bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 bg-clip-text text-transparent">Compare the same dish</h2>
          <p className="text-zinc-500 text-sm mb-6">See which platform is cheaper for the exact same restaurant</p>
          <form onSubmit={(e) => { e.preventDefault(); searchDish(); }} className="flex gap-2 max-w-lg mx-auto" data-testid="search-form">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input value={dishName} onChange={(e) => setDishName(e.target.value)} placeholder="Search a dish... e.g. Biryani" className="pl-9 h-11 bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 rounded-xl" data-testid="dish-search-input" />
            </div>
            <Button type="submit" disabled={loading || !location} className="h-11 px-5 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 font-semibold" data-testid="search-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" />Compare</>}
            </Button>
          </form>
        </div>

        {/* Trending */}
        {!results && !loading && (
          <div className="mb-10" data-testid="trending-section">
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-3">Popular searches</p>
            <div className="flex flex-wrap gap-2">
              {TRENDING.map(d => <button key={d} onClick={() => searchDish(d)} className="px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 text-xs text-zinc-400 hover:text-white hover:border-zinc-600 transition">{d}</button>)}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <div className="inline-flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-2xl px-6 py-4">
              <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
              <div className="text-left"><p className="font-medium text-sm">Comparing prices...</p><p className="text-xs text-zinc-500">Scraping Swiggy & Zomato in real-time</p></div>
            </div>
          </div>
        )}

        {/* Results */}
        {results && !loading && (
          <div className="space-y-6" data-testid="results-section">

            {/* Best Deal */}
            {bd && bd.swiggy && bd.zomato && (
              <Card className="bg-gradient-to-r from-green-950/40 to-emerald-950/40 border-green-800/20" data-testid="best-deal">
                <CardContent className="p-5 flex items-center gap-5">
                  <div className="w-12 h-12 rounded-2xl bg-green-500/20 flex items-center justify-center shrink-0"><TrendingDown className="w-6 h-6 text-green-400" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-green-400 font-medium mb-0.5">Best Deal</p>
                    <h3 className="text-lg font-bold truncate">{bd.restaurant_name}</h3>
                    <p className="text-xs text-zinc-400">{bd.swiggy.dish_name} {bd.area ? `| ${bd.area}` : ''}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="flex items-center gap-3">
                      <div><p className="text-[10px] text-orange-400">Swiggy</p><p className="text-lg font-bold">&#8377;{bd.swiggy.price}</p></div>
                      <span className="text-zinc-600">vs</span>
                      <div><p className="text-[10px] text-red-400">Zomato</p><p className="text-lg font-bold">&#8377;{bd.zomato.price}</p></div>
                    </div>
                    {bd.savings_pct > 0 && <Badge className="mt-1 bg-green-500/20 text-green-400 text-[10px]">Save {bd.savings_pct}% on {bd.cheaper_on}</Badge>}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Nutrition */}
            {n && n.calories && (
              <Card className="bg-zinc-900/50 border-zinc-800" data-testid="nutrition-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Heart className="w-4 h-4 text-pink-400" /><span className="text-sm font-medium">Nutrition</span>
                    <Badge className={`ml-auto text-[10px] ${n.health_score >= 7 ? 'bg-green-500/20 text-green-400' : n.health_score >= 4 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>{n.health_score}/10</Badge>
                  </div>
                  <div className="grid grid-cols-5 gap-2 mb-2">
                    <NutPill label="Cal" value={n.calories} unit="" color="text-orange-400" />
                    <NutPill label="Protein" value={n.protein_g} unit="g" color="text-blue-400" />
                    <NutPill label="Carbs" value={n.carbs_g} unit="g" color="text-yellow-400" />
                    <NutPill label="Fat" value={n.fat_g} unit="g" color="text-red-400" />
                    <NutPill label="Fiber" value={n.fiber_g} unit="g" color="text-green-400" />
                  </div>
                  {n.health_tip && <p className="text-[11px] text-zinc-500 bg-zinc-800/50 rounded-lg px-3 py-2"><span className="text-yellow-400">Tip:</span> {n.health_tip}</p>}
                </CardContent>
              </Card>
            )}

            {/* Matched Restaurants - Same restaurant on both */}
            {results.matched?.length > 0 && (
              <div data-testid="matched-section">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <h3 className="text-sm font-semibold">Same Restaurant on Both ({results.matched.length})</h3>
                </div>
                <div className="space-y-3">
                  {results.matched.map((m, idx) => (
                    <MatchedCard key={`match-${idx}`} match={m} />
                  ))}
                </div>
              </div>
            )}

            {/* Swiggy Only */}
            {results.swiggy_only?.length > 0 && (
              <div data-testid="swiggy-only-section">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 rounded bg-orange-500/20 flex items-center justify-center"><span className="text-orange-400 text-[10px] font-bold">S</span></div>
                  <h3 className="text-sm font-semibold text-zinc-400">Swiggy Only ({results.swiggy_only.length})</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {results.swiggy_only.map((m, idx) => (
                    <SingleCard key={`sw-${idx}`} match={m} platform="swiggy" />
                  ))}
                </div>
              </div>
            )}

            {/* Zomato Only */}
            {results.zomato_only?.length > 0 && (
              <div data-testid="zomato-only-section">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 rounded bg-red-500/20 flex items-center justify-center"><span className="text-red-400 text-[10px] font-bold">Z</span></div>
                  <h3 className="text-sm font-semibold text-zinc-400">Zomato Only ({results.zomato_only.length})</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {results.zomato_only.map((m, idx) => (
                    <SingleCard key={`zm-${idx}`} match={m} platform="zomato" />
                  ))}
                </div>
              </div>
            )}

            {results.total_results === 0 && (
              <div className="text-center py-16 text-zinc-600"><p className="text-lg mb-2">No results found</p><p className="text-sm">Try a different dish or check your location</p></div>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-zinc-800/50 mt-12">
        <div className="max-w-5xl mx-auto px-4 py-4 text-center text-[11px] text-zinc-700">
          Swiggy prices are exact dish prices. Zomato prices are estimated from average order cost. Actual prices may vary.
        </div>
      </footer>
    </div>
  );
};

// --- Sub-components ---

const NutPill = ({ label, value, unit, color }) => (
  <div className="bg-zinc-800/50 rounded-lg p-2 text-center border border-zinc-700/30">
    <p className={`text-lg font-bold ${color}`}>{value}<span className="text-[10px] text-zinc-500">{unit}</span></p>
    <p className="text-[10px] text-zinc-500">{label}</p>
  </div>
);

const MatchedCard = ({ match }) => {
  const sw = match.swiggy;
  const zm = match.zomato;
  const cheaper = match.cheaper_on;

  return (
    <Card className={`overflow-hidden ${match.savings_pct > 10 ? 'border-green-800/30 bg-green-950/10' : 'bg-zinc-900/50 border-zinc-800/50'}`} data-testid="matched-card">
      <CardContent className="p-0">
        {/* Restaurant header - name prominently displayed */}
        <div className="px-4 pt-4 pb-3 border-b border-zinc-800/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {match.image_url && <img src={match.image_url} alt="" className="w-10 h-10 rounded-xl object-cover shrink-0" onError={(e) => e.target.style.display = 'none'} />}
              <div className="min-w-0">
                <h4 className="font-bold text-base text-white">{match.restaurant_name}</h4>
                <p className="text-xs text-zinc-500">{match.area}{match.rating ? ` | ` : ''}{match.rating ? <><Star className="w-3 h-3 inline text-yellow-400 fill-yellow-400" /> {match.rating}</> : ''}</p>
              </div>
            </div>
            {match.savings_pct > 0 && <Badge className="bg-green-500/20 text-green-400 text-xs shrink-0">Save {match.savings_pct}%</Badge>}
          </div>
        </div>

        {/* Price comparison row */}
        <div className="grid grid-cols-[1fr,auto,1fr] items-stretch gap-0">
          {/* Swiggy */}
          <div className={`p-4 flex flex-col ${cheaper === 'swiggy' ? 'bg-green-950/20' : ''}`}>
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-xs font-bold text-orange-400">SWIGGY</span>
              {sw?.price_type === 'exact' && <span className="text-[9px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">exact price</span>}
            </div>
            {sw ? (
              <>
                <p className="text-2xl font-bold text-white">&#8377;{sw.price}</p>
                <p className="text-xs text-zinc-400 mt-1">{sw.dish_name}</p>
                {sw.delivery_time && <p className="text-[11px] text-zinc-600 flex items-center gap-1 mt-1"><Clock className="w-3 h-3" />{sw.delivery_time}</p>}
              </>
            ) : <p className="text-zinc-600 text-sm">Not available</p>}
            {cheaper === 'swiggy' && <p className="text-xs text-green-400 font-semibold mt-2">CHEAPER</p>}
            {sw?.order_url && (
              <a
                href={sw.order_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold transition"
                data-testid="order-on-swiggy-btn"
              >
                Order on Swiggy <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          {/* VS divider */}
          <div className="flex flex-col items-center justify-center px-3 bg-zinc-800/20">
            <span className="text-xs text-zinc-500 font-bold">VS</span>
            {match.price_diff > 0 && <span className="text-[10px] text-zinc-500 mt-1">&#8377;{match.price_diff}</span>}
          </div>

          {/* Zomato */}
          <div className={`p-4 flex flex-col ${cheaper === 'zomato' ? 'bg-green-950/20' : ''}`}>
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-xs font-bold text-red-400">ZOMATO</span>
              {zm && zm.price_type === 'ai_estimated' && <span className="text-[9px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">AI estimated</span>}
            </div>
            {zm ? (
              <>
                <p className="text-2xl font-bold text-white">&#8377;{zm.price}</p>
                <p className="text-xs text-zinc-400 mt-1">{zm.dish_name}</p>
                {zm.delivery_time && <p className="text-[11px] text-zinc-600 flex items-center gap-1 mt-1"><Clock className="w-3 h-3" />{zm.delivery_time}</p>}
              </>
            ) : <p className="text-zinc-600 text-sm">Not available</p>}
            {cheaper === 'zomato' && <p className="text-xs text-green-400 font-semibold mt-2">CHEAPER</p>}
            {zm?.order_url && (
              <a
                href={zm.order_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-semibold transition"
                data-testid="order-on-zomato-btn"
              >
                Order on Zomato <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SingleCard = ({ match, platform }) => {
  const data = platform === 'swiggy' ? match.swiggy : match.zomato;
  const color = platform === 'swiggy' ? 'orange' : 'red';
  if (!data) return null;
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-3 flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className={`text-[10px] font-bold text-${color}-400`}>{platform.toUpperCase()}</span>
          {data.price_type === 'exact' && <span className="text-[8px] text-zinc-600 bg-zinc-800 px-1 rounded">exact</span>}
        </div>
        <p className="font-medium text-sm truncate">{match.restaurant_name}</p>
        <p className="text-[10px] text-zinc-500 truncate">{data.dish_name}{match.area ? ` | ${match.area}` : ''}</p>
      </div>
      <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
        <p className="text-lg font-bold leading-none">&#8377;{data.price}</p>
        {match.rating && <p className="text-[10px] text-zinc-500"><Star className="w-2.5 h-2.5 inline text-yellow-400 fill-yellow-400" /> {match.rating}</p>}
        {data.order_url && (
          <a
            href={data.order_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md ${platform === 'swiggy' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-red-500 hover:bg-red-600'} text-white text-[10px] font-semibold transition`}
            data-testid={`single-order-${platform}-btn`}
          >
            Order <ExternalLink className="w-2.5 h-2.5" />
          </a>
        )}
      </div>
    </div>
  );
};

export default FoodPriceCompare;
